/**
 * Notifications — topic catalog + per-caller subscriptions (Layer 6, F15).
 * Self-service for every role: a caller opts in/out per topic (default-routed
 * by role until overridden).
 *
 * Maps to the notifications module:
 *   - GET    /notifications/topics            → topic catalog
 *   - GET    /notifications/subscriptions     → caller's effective per-topic state
 *   - PUT    /notifications/subscriptions/{k} → set opt-in/out (no X-Reason)
 */

import {
  Alert,
  Badge,
  Group,
  Paper,
  Skeleton,
  Stack,
  Switch,
  Text,
  ThemeIcon,
  Title,
  Tooltip,
} from "@mantine/core";
import { notifications as notify } from "@mantine/notifications";
import { IconAlertCircle, IconBellRinging } from "@tabler/icons-react";
import { useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import {
  useNotificationSubscriptions,
  useNotificationTopics,
  useSetSubscription,
} from "../api/hooks";
import type { SubscriptionState, TopicInfo } from "../api/types";
import { useUser } from "../auth";
import { formatApiError } from "../lib/errors";
import { queryKeys } from "../lib/queryKeys";

const SEVERITY_COLOR: Record<string, string> = {
  critical: "red",
  warning: "orange",
  info: "blue",
};

function severityColor(severity: string): string {
  return SEVERITY_COLOR[severity] ?? "gray";
}

export function NotificationsPage() {
  return (
    <Stack gap="xl">
      <Stack gap={4}>
        <Group gap="sm">
          <ThemeIcon variant="light" color="indigo" radius="md" size={34}>
            <IconBellRinging size={18} />
          </ThemeIcon>
          <Title order={2}>Notifications</Title>
        </Group>
        <Text size="sm" c="dimmed">
          Choose which compliance events email you. Topics are routed to roles by default; your
          per-topic choice overrides that default for your account.
        </Text>
      </Stack>

      <SubscriptionsSection />
    </Stack>
  );
}

// ── Topics + subscriptions ──────────────────────────────────────────────────

function SubscriptionsSection() {
  const topicsQ = useNotificationTopics();
  const subsQ = useNotificationSubscriptions();

  // Join the catalog with the caller's subscription state by topic_key.
  const rows = useMemo(() => {
    const topics = topicsQ.data ?? [];
    const byKey = new Map<string, SubscriptionState>(
      (subsQ.data ?? []).map((s) => [s.topic_key, s]),
    );
    return topics.map((topic) => ({ topic, sub: byKey.get(topic.key) ?? null }));
  }, [topicsQ.data, subsQ.data]);

  const isPending = topicsQ.isPending || subsQ.isPending;
  const error = topicsQ.error ?? subsQ.error;

  return (
    <Stack gap="sm">
      <Title order={4}>Topics</Title>
      {error ? (
        <LoadError context="topics" error={error} />
      ) : isPending ? (
        <Skeleton height={220} radius="md" />
      ) : (
        <Paper withBorder radius="md">
          <Stack gap={0}>
            {rows.map(({ topic, sub }, i) => (
              <TopicRow key={topic.key} topic={topic} sub={sub} divider={i < rows.length - 1} />
            ))}
          </Stack>
        </Paper>
      )}
    </Stack>
  );
}

function TopicRow({
  topic,
  sub,
  divider,
}: {
  topic: TopicInfo;
  sub: SubscriptionState | null;
  divider: boolean;
}) {
  const setSub = useSetSubscription();
  const queryClient = useQueryClient();
  const me = useUser();
  // Before subscriptions resolve, fall back to the BE's role-based default:
  // a topic is on-by-default only if the caller's role is a default recipient
  // (matches topic_default_roles on the BE) — not merely if any role is.
  const roleDefault = me ? topic.default_roles.includes(me.role) : false;
  const enabled = sub?.enabled ?? roleDefault;
  const isOverride = sub?.is_override ?? false;

  const onToggle = async (next: boolean) => {
    try {
      await setSub.mutateAsync({ topicKey: topic.key, enabled: next });
      await queryClient.invalidateQueries({
        queryKey: queryKeys.notifications.subscriptions(),
      });
    } catch (err) {
      notify.show({
        title: "Couldn't update subscription",
        message: formatApiError(err),
        color: "red",
        autoClose: 5000,
      });
    }
  };

  return (
    <Group
      justify="space-between"
      wrap="nowrap"
      px="md"
      py="sm"
      style={divider ? { borderBottom: "1px solid var(--mantine-color-gray-2)" } : undefined}
    >
      <Group gap="sm" wrap="nowrap" style={{ minWidth: 0 }}>
        <Badge color={severityColor(topic.severity)} variant="light" size="sm" w={84}>
          {topic.severity}
        </Badge>
        <Stack gap={2} style={{ minWidth: 0 }}>
          <Text size="sm" fw={600} truncate>
            {topic.name}
          </Text>
          <Text size="xs" c="dimmed" ff="monospace" truncate>
            {topic.key} · default: {topic.default_roles.join(", ") || "none"}
          </Text>
        </Stack>
      </Group>
      <Group gap="sm" wrap="nowrap">
        {isOverride ? (
          <Tooltip label="You've overridden the role default for this topic.">
            <Badge variant="outline" color="gray" size="xs">
              overridden
            </Badge>
          </Tooltip>
        ) : (
          <Badge variant="transparent" color="gray" size="xs">
            default
          </Badge>
        )}
        <Switch
          data-tour="notification-toggle"
          checked={enabled}
          onChange={(e) => onToggle(e.currentTarget.checked)}
          disabled={setSub.isPending}
          aria-label={`Toggle ${topic.name}`}
        />
      </Group>
    </Group>
  );
}

function LoadError({ context, error }: { context: string; error: unknown }) {
  return (
    <Alert color="red" variant="light" icon={<IconAlertCircle size={16} />}>
      <Text size="sm">
        Couldn't load {context}: {formatApiError(error)}
      </Text>
    </Alert>
  );
}
